// Canonical cashier role slug, per OG resolution: the cashier authenticates
// as 'pos' / 'view:pos' — NOT a 'cashier' slug. Screen-specific domain types
// (order entry, product lookup) land with the 1b screens bench.

export const CASHIER_ROLE = 'pos' as const
export const CASHIER_SCOPE = 'view:pos' as const

export type HttpMethod = 'POST' | 'PUT' | 'PATCH'

export type QueuedRequestBody = Record<string, unknown>

export interface QueuedRequest {
  uuid: string
  method: HttpMethod
  endpoint: string
  body: QueuedRequestBody
  status: 'pending' | 'sent' | 'failed'
  failReason?: string
  createdAt: number
  /** R413: the id of the person signed in when this was queued. Only that person sends it. */
  sub?: string
}

// bench-CASHIER-TILL — the pickup-collect screen. amountUsd only; no FX
// reference (see cash-records.service.ts on the server side for why).
export interface CashCollectionRequest {
  orderId: string
  amountUsd: number
}

// bench-CASHIER-LOOKUP — an order as the till surfaces it: off the ready-for-pickup
// tap-list (GET /admin/orders/ready-for-pickup) or the phone/short-code lookup
// (GET /admin/customers/order-lookup). Same shape either way — the screen renders
// both lists identically. totalAmount is `expected`, read-only on the screen (§3f).
export interface LookupableOrder {
  id: string
  shortCode: string | null
  totalAmount: number
  customerName: string | null
  phoneNormalized: string | null
}

// R78 — an item on Nour's final-check list. No phone, no address: Nour
// checks contents (GET /admin/orders/awaiting-final-check). R197 (d): the row carries money
// so the confirm can ask when it moved; the screen still shows none.
export interface FinalCheckItem {
  nameEn: string | null
  variantNameEn: string | null
  quantity: number
}

export interface FinalCheckOrder {
  id: string
  shortCode: string | null
  fulfillmentMethod: 'pickup' | 'delivery' | null
  placedAt: string
  customerName: string | null
  items: FinalCheckItem[]
  // Typed as the server sends them, but read through classify() in lib/final-check.ts.
  moneyChanged: boolean
  totalNow: string
  totalAtPlacement: string
  currency: string
}

// R462 - an order in flight on the till (GET /admin/orders/till-in-flight): from confirmation
// until final check. productType 'meal' is the kitchen's line, anything else the shelf's.
export type KitchenTaskStatus = 'PENDING' | 'IN_PROGRESS' | 'BLOCKED' | 'COMPLETED' | 'CANCELLED'

export interface InFlightLine {
  nameEn: string | null
  variantNameEn: string | null
  quantity: number
  productType: string | null
  /** Null on a shelf line, and on a meal line the kitchen has not made a task for yet. */
  kitchenTaskStatus: KitchenTaskStatus | null
}

export interface InFlightOrder {
  id: string
  shortCode: string | null
  status: string
  fulfillmentMethod: 'pickup' | 'delivery' | null
  placedAt: string
  customerName: string | null
  lines: InFlightLine[]
}

// R4 — Nour's "Send out" tap-list (GET /dispatch/ready). No phone, no address, no total.
export interface ReadyToSendOrder {
  id: string
  shortCode: string | null
  customerName: string | null
  deliveryZoneName: string | null
  readyAt: string
}

// R4 — GET /dispatch/drivers. No phone, no username.
export interface DispatchDriver {
  id: string
  name: string
  busy: boolean
}
